import React, { useEffect, useState, useMemo } from "react";
import { Field, Formik, Form } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import AppInput from "components/Input/AppInput";
import { Box, FormGroup, Grid, makeStyles, Typography } from "@material-ui/core";
import AppColor from "utilities/AppColor";
import AppButton from "components/CustomButtons/AppButton";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import useResponsiveFontSize from "./responseFonSize";
import visaIcon from "../../../../assets/img/visa_icon.png";
import masterCardIcon from "../../../../assets/img/mastercard_icon.png";
import americanExpressIcon from "../../../../assets/img/american_express_icon.png";
import dinnerClubIcon from "../../../../assets/img/club_diners_icon.png";
import discoverIcon from "../../../../assets/img/discover_icon.png";
import jcbIcon from "../../../../assets/img/jcb_icon.png";
import unionPayIcon from "../../../../assets/img/unionpay_icon.png";
import unknowCardIcon from "../../../../assets/img/unknown_card_icon.png";
import AppConfig from "config/AppConfig";
import AppUtil from "utilities/AppUtil";

const { PATTERNS } = AppConfig;
const ListCard = [
  {
    id: 1,
    value: "UNKNOWN",
    icon: unknowCardIcon,
  },
  {
    id: 2,
    value: "VISA",
    icon: visaIcon,
  },
  {
    id: 3,
    value: "MASTERCARD",
    icon: masterCardIcon,
  },
  {
    id: 4,
    value: "AMERICAN_EXPRESS",
    icon: americanExpressIcon,
  },
  {
    id: 5,
    value: "DINERS_CLUB",
    icon: dinnerClubIcon,
  },
  {
    id: 6,
    value: "DISCOVER",
    icon: discoverIcon,
  },
  {
    id: 7,
    value: "JCB",
    icon: jcbIcon,
  },
  {
    id: 8,
    value: "UNIONPAY",
    icon: unionPayIcon,
  },
];

const useOptions = () => {
  const fontSize = useResponsiveFontSize();
  const options = useMemo(
    () => ({
      hidePostalCode: true,
      style: {
        base: {
          fontSize: '16px',
          color: "#424770",
          letterSpacing: "0.025em",
          fontFamily: "Source Code Pro, monospace",
          "::placeholder": {
            color: "#aab7c4",
          },
        },
        invalid: {
          color: "#9e2146",
        },
      },
    }),
    [fontSize]
  );

  return options;
};

const BillingCompanyForm = (props) => {
  const {
    onSubmit,
    isSubmitting,
    companyDetail = {},
    changeProgressStatus,
    display,
    // hideLeftControl,
    isCreate,
    disabled,
  } = props;
  const stripe = useStripe();
  const { t } = useTranslation();
  const options = useOptions();
  const elements = useElements();
  const isMobile = AppUtil.mobileAndTabletCheck();

  const [initForm, setInitForm] = useState({
    cardHolder: "",
    cardNumber: "",
  });
  const [isOpenEdit, setOpenEdit] = useState(false);
  const [errorCard, setErrorCard] = useState('');
  const [isLoading, setLoading] = useState(false);
  const validationSchema = Yup.object().shape({
    cardHolder: Yup.string().required("validation.required").max(255, "validation.max_length_255").matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces').nullable()
  });

  const { company = {} } = companyDetail;

  const { id: companyId, cardHolder = "", cardType, cardNumber, cardExpDate } = company;

  const styles = makeStyles(() => ({
    title: {
      display: "flex",
      margin: "2rem 0 1rem 0",
      "& h5": {
        color: AppColor.mainColor,
      },
    },
    control: {
      display: "flex",
      flexWrap: "wrap",
      flexDirection: "row",
      justifyContent: companyId ? "space-between" : "flex-end",
      "& a": {
        cursor: "pointer",
        textDecoration: "underline",
        color: AppColor.pink,
      },
    },
  }));
  const classes = styles();
  useEffect(() => {
    if (companyDetail && Object.keys(companyDetail).length > 0) {
      const initial = {
        cardHolder,
      };
      validationSchema.isValid(initial).then((valid) => {
        setTimeout(() => {
          changeProgressStatus(2, valid);
        }, 300);
      });
      setInitForm(initial);
    }
  }, [companyDetail]);

  const handleSubmitForm = async (form) => {
    setLoading(true);
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }
    const cardElement = elements.getElement("card");
    const result = await stripe.createToken(cardElement).then((payload) => {
      if (payload && payload.token && payload.token.id) {
        return payload.token.id;
      }
      if (payload.error && payload.error.message) {
        setErrorCard(payload.error.message);
      }
    }).catch((err) => {
      console.log(err);
    });

    if (result) {
      const params = {
        ...form,
        companyId,
        cardToken: result,
      };
      onSubmit(params);
      setOpenEdit(false);
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      onSubmit={!isLoading && handleSubmitForm}
      initialValues={initForm}
      validationSchema={validationSchema}
    >
      {() => {
        // console.log("values", formikProps);
        return (
          <Form className={`${!display && "hidden"}`}>
            <Box className={classes.title}>
              <Typography variant="h5">
                {t("title.billingInformation")}
              </Typography>
            </Box>
            <Field
              component={AppInput}
              label={t("label.cardHolder")}
              name={"cardHolder"}
              type={'text'}
              disabled={(disabled || (!isOpenEdit && !isCreate && cardHolder) || (!isOpenEdit && isCreate && cardHolder)) ? true : false}
            />
            <FormGroup>
              <label>{t("label.cardNumber")} 
              {isMobile && ListCard.map((card) => {
                if (card.value === cardType) {
                  return (
                    <React.Fragment key={card.id}>
                      <img key={`${card.id}-img`} src={card.icon} style={{ width: "40px", marginRight: '20px' }} />
                    </React.Fragment>
                  );
                }
              })}
              </label>
              <div
                className="w-100"
                style={{
                  display: (!isOpenEdit && cardNumber) ? 'flex' : '',
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  height: "40px",
                  border: errorCard ? "1px solid #dc3545" : "1px solid rgba(0, 0, 0, 0.2)",
                  borderRadius: "5px",
                  boxShadow: "0px 4px 4px rgb(0 0 0 / 25%)",
                  boxSizing: "border-box",
                  marginBottom: "20px",
                  backgroundColor: (!isOpenEdit && cardNumber) ? "#E9ECEF" : "",
                  padding:
                    (!isOpenEdit && cardNumber)
                      ? "0px 0px 0px 20px"
                      : "7px 0px 0px 20px",
                }}
              >
                {((!isCreate && !isOpenEdit && cardType && cardNumber) || (isCreate && !isOpenEdit && cardNumber)) &&
                  ListCard.map((card) => {
                    if (card.value === cardType) {
                      return (
                        <React.Fragment key={card.id}>
                          {!isMobile && <img key={`${card.id}-img`} src={card.icon} style={{ width: "40px", marginRight: '20px' }} />}
                          <div style={{ display: 'inline-block', width: !isMobile ? '90%' : '100%', fontSize: '16px', marginTop: '5px' }}>
                            <Grid container>
                              <Grid item xs={6}>
                                {`****  ****  ${cardNumber}`}
                              </Grid>
                              <Grid item xs={5} style={{ textAlign: 'end' }}>
                                {cardExpDate}
                              </Grid>
                            </Grid>
                          </div>
                        </React.Fragment>
                      );
                    }
                  })}
                {((isCreate && !cardNumber) || (isCreate && cardNumber && isOpenEdit) || (!isCreate && isOpenEdit) || (!isCreate && !cardNumber)) && (
                  <CardElement
                    options={options}
                    // onReady={() => {
                    //   console.log("CardElement [ready]");
                    // }}
                    onChange={(event) => {
                      if (event.error && event.error.message) {
                        setErrorCard(event.error.message);
                      } else {
                        setErrorCard('');
                      }
                      // console.log("CardElement [change]", event);
                    }}
                  // onBlur={() => {
                  //   console.log("CardElement [blur]");
                  // }}
                  // onFocus={() => {
                  //   console.log("CardElement [focus]");
                  // }}
                  />
                )}
              </div>
              <div style={{ color: '#dc3545', marginBottom: '10px', fontSize: '80%' }}>
                <span>{errorCard}</span>
              </div>
            </FormGroup>
            {
              <FormGroup className={classes.control}>
                {((!isCreate && cardNumber && !isOpenEdit) || (isCreate && cardNumber && !isOpenEdit)) && (
                  <AppButton
                    label={`${t("button.edit")}`}
                    rounder
                    type="button"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    onClick={() => {
                      setOpenEdit(true);
                    }}
                  />
                )}
                {((isCreate && !cardNumber) ||
                  (isCreate && cardNumber && isOpenEdit) ||
                  (!isCreate && cardNumber && isOpenEdit) ||
                  (!isCreate && !cardNumber)) && (
                    <AppButton
                      label={`${t("button.saveAndContinue")}`}
                      rounder
                      type="submit"
                      isLoading={isSubmitting}
                      disabled={isLoading}
                    />
                  )}
                {isOpenEdit && (
                  <AppButton
                    label={`${t("button.cancel")}`}
                    rounder
                    type="button"
                    buttonColor={'grey'}
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    onClick={() => {
                      setOpenEdit(false);
                      if (errorCard) {
                        setErrorCard('');
                      }
                    }}
                  />
                )}
              </FormGroup>
            }
          </Form>
        );
      }}
    </Formik>
  );
};

BillingCompanyForm.propTypes = {
  onSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  hideLeftControl: PropTypes.bool,
  display: PropTypes.bool,
  disabled: PropTypes.bool,
  companyDetail: PropTypes.object,
  changeProgressStatus: PropTypes.func,
  isCreate: PropTypes.bool,
};

export default BillingCompanyForm;